import React, { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, Illustration, toast } from '@node-space/ui-components'
import { useDeleteApiKeyMutation } from 'hooks/mutations'
import { ApiKey } from 'types/apiKeys'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

type ApiKeySuccessModalProps = {
  selectedApiKey: ApiKey
  handleCloseModal: () => void
}

const ApiKeyDeleteModal = ({ selectedApiKey, handleCloseModal }: ApiKeySuccessModalProps) => {
  const { description } = selectedApiKey || {}
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { mutate, isPending } = useDeleteApiKeyMutation()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.DELETE_API_KEY_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const onDeleteClicked = async () => {
    mutate(selectedApiKey?.tokenValue, {
      onSuccess: async () => {
        toast({ header: t('manageApiKeys.apiKeyDeleted'), variant: 'info' })
        track.Amp.track(AmplitudeEvent.EOF_DELETE_API_KEY_SUCCESS, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.CLICK,
        })
        handleCloseModal()
        await queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
      },
      onError: () => {
        toast({ header: t('apiKeyDeleteError'), variant: 'error' })
      },
    })
  }

  return (
    <Dialog
      title={t('manageApiKeys.deleteAPIKey', { keyName: description })}
      open
      onClose={handleCloseModal}
      Illustration={<Illustration name="CloudLightning" />}
      ActionButtons={[
        <Button
          key="deleteKey"
          variant="destructive"
          testId={t('deleteKey')}
          onClick={onDeleteClicked}
          loading={isPending}
        >
          {t('delete')}
        </Button>,
        <Button key="cancel" onClick={handleCloseModal} variant="secondary" testId="close-button">
          {t('cancel')}
        </Button>,
      ]}
    >
      <p className="text-foreground-prominent text-body-regular text-center">
        {t('manageApiKeys.deleteDescription')}
      </p>
    </Dialog>
  )
}

export default ApiKeyDeleteModal
